<style lang="less" scoped>
.form-control:not([type="checkbox"]) {
    line-height: 1.2;
}
</style>
<template>
    <div class="modal-content" ref="container">
        <form>
            <div class="modal-header clearfix">
                <h3 class="modal-title pull-left">Import {{ code }}</h3>
                <button type="button" aria-label="Close" class="close icon icon-close" @click="close()" />
            </div>
            <div class="modal-body">
                <div describedby-text class="alert alert-danger">This will *REPLACE* all existing tenant and sub-tenant settings, submissions, home page data, pages definitions, address book data, etc.</div>
                <div class="clearfix row">
                    <input type="hidden" name="dummy" value="dummy" />
                    <div class="form-group col-12 col-lg-6">
                        <label class="form-label" for="uploadFile">File to import</label>
                        <input class="form-control" type="file" name="file" id="uploadFile" ref="fileRef" multiple required />
                    </div>
                    <div class="form-group col-12 col-lg-6">
                        <label class="form-label" for="uploadFilename">Filename to import</label>
                        <input class="form-control" type="text" name="uploadFilename" id="uploadFilename" v-model="filename" />
                    </div>
                    <div class="form-group col-12">
                        <label class="form-label" for="confirmCode">To confirm, enter the tenant code</label>
                        <input class="form-control" type="text" name="confirmCode" id="confirmCode" v-model="confirmCode" />
                    </div>
                    <div class="form-group col-12 col-lg-6">
                        <label class="form-label" for="uploadMappingFile">Mapping File to import</label>
                        <input class="form-control" type="file" name="mappingFile" id="uploadMappingFile" ref="mappingFileRef" />
                    </div>
                    <div class="form-group col-12 col-lg-6">
                        <label class="form-label" for="uploadMappingFilename">Mapping Filename to import</label>
                        <input class="form-control" type="text" name="uploadMappingFilename" id="uploadMappingFilename" v-model="mappingFilename" />
                    </div>
                    <div class="form-group col-12 col-lg-4">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" name="generateMappingFile" id="generateMappingFile" v-model="generateMappingFile" />
                            <label class="form-check-label" for="generateMappingFile">Generate mapping file</label>
                        </div>
                    </div>
                    <div class="form-group col-12 col-lg-4">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" name="keepOpen" id="keepOpen" v-model="keepOpen" />
                            <label class="form-check-label" for="keepOpen">Keep open</label>
                        </div>
                    </div>
                    <div class="form-group col-12 col-lg-4">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" name="ignoreUrlSlugDifferences" id="ignoreUrlSlugDifferences" v-model="ignoreUrlSlugDifferences" />
                            <label class="form-check-label" for="ignoreUrlSlugDifferences">Skip Url Slug Validation</label>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer row no-gutters text-right">
                <div class="col-12">
                    <button type="button" class="btn btn-primary" :disable="confirm.status == 'in-progress'" @click="confirmUpload">
                        Import
                        <span v-if="confirm.status == 'fail'" style="color: red" class="icon icon-attention"></span>
                        <LoadingWheel v-if="confirm.status == 'in-progress'" style="position: relative; top: -2px; display: inline-block" :width="16"></LoadingWheel>
                    </button>
                    <button type="button" class="btn btn-bare" @click="close()">Cancel</button>
                </div>
                <div class="col-12">
                    <p class="help-block pb-0 mb-0" v-for="error in confirm.errors">{{ error }}</p>
                </div>
            </div>
        </form>
    </div>
</template>
<script lang="ts">
import { defineComponent, inject, ref, SetupContext } from "vue";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import delay from "@/lib/delayPromise";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { useMainStore } from "@/mainStore";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";
import { TenantImportResults } from "@cs-ts/tenant-import-results";
type Confirm = { status: string; errors: string[] };
type Props = { code: string; confirmCallback: (uploadData: Record<string, string>) => void } & DialogProps;

export default defineComponent({
    props: ["code", "confirmCallback", "triggerElement"],
    components: {
        LoadingWheel,
    },
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);
        const mainStore = useMainStore();

        const fileRef = ref<HTMLInputElement>(null!);
        const mappingFileRef = ref<HTMLInputElement>(null!);

        const confirm = ref<Confirm>({
            status: null!,
            errors: [],
        });
        const filename = ref<string>(null!);
        const mappingFilename = ref<string>(null!);
        const keepOpen = ref(false);
        const ignoreUrlSlugDifferences = ref(false);
        const generateMappingFile = ref(false);
        const confirmCode = ref<string>(null!);

        const close = () => {
            emit("close");
        };
        const confirmUpload = async () => {
            if (await uploadFile()) {
                await props.confirmCallback({});
                if (!keepOpen.value) {
                    close();
                }
            }
        };
        const uploadFile = async () => {
            const typeData = confirm.value;
            if (confirmCode.value != props.code) {
                typeData.errors = ["Confirm code does not match expected code"];
                typeData.status = "fail";
                return;
            }
            const files = fileRef.value.files!;
            const hasFileName = filename.value != null && !/^\s*$/.test(filename.value);
            if (files.length == 0 && !hasFileName) {
                typeData.errors = ["No file supplied"];
                typeData.status = "fail";
                return;
            }
            const mappingFiles = mappingFileRef.value.files!;
            const hasMappingFileName = mappingFilename.value != null && !/^\s*$/.test(mappingFilename.value);
            if (mappingFiles.length == 0 && !hasMappingFileName) {
                // Not a problem
            }

            const uploadUrl = await mainStore.getUrl("adminTenantManagementImport", { code: props.code });
            typeData.errors = [];
            const upload = async (file: File, notFinal: boolean) => {
                typeData.status = "in-progress";
                const uploadData = new FormData();
                if (file != null) {
                    uploadData.append("file", file, file.name);
                }
                if (hasFileName) {
                    uploadData.append("filename", filename.value);
                }
                if (mappingFiles.length > 0) {
                    const newMappingFile = mappingFiles[0];
                    uploadData.append("mappingsFile", newMappingFile, newMappingFile.name);
                }
                if (hasMappingFileName) {
                    uploadData.append("mappingsFilename", mappingFilename.value);
                }
                uploadData.append("ignoreUrlSlugChanges", ignoreUrlSlugDifferences.value.toString());
                uploadData.append("notFinal", notFinal.toString());

                try {
                    let response = await fetch(uploadUrl, {
                        method: "POST",
                        headers: await getXsftHeader(mainStore),
                        credentials: "same-origin",
                        body: uploadData,
                    });
                    //if (response.status == 503) {
                    //    typeData.status = 'fail';
                    //    typeData.errors = ['Timeout likely occurred. Wait before checking import success'];
                    //    return;
                    //}
                    throwIfNonSuccessStatus(response);
                    let responseJson: TenantImportResults & { resultToken?: string } = await response.json();
                    if (responseJson.resultToken) {
                        const statusUrl = await mainStore.getUrl("adminTenantManagementImportStatus", { code: props.code, resultToken: responseJson.resultToken });
                        const delayTimes = [1, 1, 1, 2, 3, 5, 8, 13];
                        let loopCheck = 0;
                        while (loopCheck++ < 1000) {
                            await delay((delayTimes.shift() || 20) * 1000);
                            response = await fetch(statusUrl);
                            throwIfNonSuccessStatus(response);
                            responseJson = await response.json();
                            if (!responseJson.resultToken) {
                                break;
                            }
                        }
                    }
                    if (responseJson.success) {
                        if (generateMappingFile.value) {
                            const link = document.createElement("a");
                            link.download = "ImportMapping-" + props.code + ".json";
                            const blob = new Blob([JSON.stringify(responseJson.mappings)], { type: "application/json" });
                            link.href = URL.createObjectURL(blob);
                            link.click();
                            URL.revokeObjectURL(link.href);
                        }
                        typeData.status = "ok";
                        return true;
                    } else {
                        typeData.status = "fail";
                        typeData.errors = typeData.errors.concat(responseJson.errors!);
                    }
                } catch (e) {
                    typeData.status = "fail";
                    typeData.errors = ["Unknown error occurred"];
                    throw e;
                }
            };
            if (files.length > 0) {
                for (let index = 0; index < files.length; index++) {
                    if (!(await upload(files[index], index < files.length - 1))) {
                        return false;
                    }
                }
                return true;
            } else {
                return await upload(null!, false);
            }
        };

        return {
            // Refs
            fileRef,
            mappingFileRef,
            container: containerRef,

            // States
            confirm,
            filename,
            mappingFilename,
            keepOpen,
            ignoreUrlSlugDifferences,
            generateMappingFile,
            confirmCode,

            // Methods
            close,
            confirmUpload,

            changePage: inject(changePageInjectKey)!,
        };
    },
});
</script>
